// pages/tasks/StatusChips.jsx
//
// Shared status/priority chip helpers + the Status/TAT cell renderer, used
// by every "polished" ticket/complaint list (ComplaintsListPage.jsx,
// DeveloperDashboard.jsx, DataAnalystDashboard.jsx, ...) so the semantic
// color mapping lives in exactly one place instead of being copy-pasted
// (and drifting) per page. These only ever map an already-computed
// status/priority string to a display className -- never change what
// status/priority a row actually has, and never touch TAT calculation
// (getTatInfo in tatUtils.js already owns that).
import React from "react";
import { Badge } from "react-bootstrap";
import { getTatInfo } from "./tatUtils";

const titleCase = (v) => String(v || "-").replace(/_/g, " ").replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

// Completed=green, In Progress=blue, Pending=amber, Cancelled/Rejected=red
// -- the one status color convention used across the whole portal.
export function statusPillClass(status) {
  const s = String(status || "").toLowerCase();
  if (["completed", "closed", "resolved"].includes(s)) return "tlp-pill tlp-pill-green";
  if (["cancelled", "rejected"].includes(s)) return "tlp-pill tlp-pill-red";
  if (["in_progress"].includes(s)) return "tlp-pill tlp-pill-blue";
  if (["pending"].includes(s)) return "tlp-pill tlp-pill-amber";
  return "tlp-pill tlp-pill-gray";
}

// Low=green, Medium=blue, High=amber, Critical=red.
export function priorityPillClass(priority) {
  const p = String(priority || "").toLowerCase();
  if (p === "low") return "tlp-pill tlp-pill-green";
  if (p === "medium") return "tlp-pill tlp-pill-blue";
  if (p === "high") return "tlp-pill tlp-pill-amber";
  if (p === "critical") return "tlp-pill tlp-pill-red";
  return "tlp-pill tlp-pill-gray";
}

export function StatusPill({ status }) {
  // bg={null} -- react-bootstrap's Badge defaults bg to "primary", which
  // appends its own !important "bg-primary" (blue) class alongside our
  // semantic tlp-pill-* one, so every pill rendered blue regardless of
  // status. See priorityPillClass for the same fix.
  return <Badge bg={null} className={statusPillClass(status)}>{titleCase(status)}</Badge>;
}

export function PriorityPill({ priority }) {
  return priority ? <Badge bg={null} className={priorityPillClass(priority)}>{titleCase(priority)}</Badge> : <span className="text-muted small">—</span>;
}

// Status pill + a TAT hint line underneath ("Completed within TAT" / "TAT
// Breached +2h 30m" / "3h left"), driven entirely by the row's existing
// due_at/status/completed_at -- see tatUtils.js#getTatInfo.
export function StatusTatCell({ row }) {
  const tat = getTatInfo(row);
  return (
    <div className="tlp-status-tat">
      <StatusPill status={row.status} />
      <span className={`tlp-tat tlp-tat-${tat.tone}`}>{tat.label}</span>
    </div>
  );
}
